/**
 * If you are working with a KW_USERS company then most of the fields below are
 * from directory.person.
 *
 * Otherwise, for YEI companies, most of the fields are from the YEI Mongo user
 * collection. Optional fields normally indicate the field is undefined in the
 * case of a YEI only company or vice versa
 *
 * KazooWebUser defined in `<root>/types/KazooWebInterfaces.d.ts`
 */
export type User = Omit<KazooWebUser, "roles"> & {
  /**
   * If you are a KW User you will get a combination of roles defined in both
   * KW and YEI. If you are a non KW User you will receive only roles defined
   * in YEI. See type for detailed role origin. If possible you should try to
   * use adminOf
   */
  roles: UserRole[];
};

/**
 * Code duplication from `api/directory/schema/schema.graphql` to avoid circular
 * dependencies.
 *
 * If you're updating this, make sure you get the graphql enum as well.
 */
export enum PeopleSegmentModule {
  BEHAVIOR_BONUS = "BEHAVIOR_BONUS",
  KW_FEEDBACK = "KW_FEEDBACK",
  KW_TALENT_REVIEW = "KW_TALENT_REVIEW",
  NEW_CHECK_INS = "NEW_CHECK_INS",
  NEW_GOALS = "NEW_GOALS",
  REWARDS = "REWARDS",
  SURVEYS = "SURVEYS",
  SYNC_UP = "SYNC_UP",
  KW_SYNC_UPS = "KW_SYNC_UPS",
  INCENTIVES = "INCENTIVES",
}

/**
 * These correspond to legacy roles right now, but we will likely repurpose most
 * or all of these for the full Work Tango platform.
 */
export enum UserRole {
  // YEI role
  ADMIN = "admin",
  ANALYST = "analyst",
  API = "api",
  CATALOG_API = "catalog_api",
  FEED = "feed",
  FINANCE = "finance",
  FULFILLER = "fulfiller",
  // This role exists in both YEI and KW
  GROUP_ADMIN = "group_admin",
  GRS_ADMIN = "grs_admin",
  PLATFORM_ADMIN = "platform_admin",
  REWARD_MANAGER = "reward_manager",
  SALES = "sales",
  SUPERADMIN = "superadmin",
  SYSTEM = "system",
  TRIAL = "trial",
  USER = "user",
  /**
   * @deprecated
   * ⚠️⚠️⚠️ Do not rely on `UserRole.MANAGER`. Check `isManager` on the
   * KazooWebUser object instead, or for users other than the current user,
   * manually query the `reports` connection for one node.
   *
   * `UserRole.MANAGER` only exists in YEI and does not exist in the PersonRoles
   * table in KW.
   *
   * This role is not a reliable indicator of whether a user is a manager. It
   * will fail to report actual managers in many cases. ⚠️⚠️⚠️
   */
  MANAGER = "manager",
  /** KW roles only */
  SURVEY_ADMIN = "survey_admin",
  // TODO (WT-15400, WT15403): Implement the UI and writeback side-effects of this role.
  /** Role that grants users UI access to Pulse app.
   *  Will also write back a pre-defined set of permissions within the Pulse app
   * so that that WTW user can access certain features in Pulse.
   */
  SURVEY_CUSTOM = "survey_custom",
  SURVEY_PEOPLE_LEADER = "survey_people_leader",
  SURVEY_FULL_REPORT_READER = "survey_full_report_reader",
  /**
   * Role that grants Pulse-only users access to the WorkTango AI Assistant
   * admin functionality.
   */
  WORKTANGO_COACH_ADMIN = "worktango_coach_admin",
  /**
   * Role that grants users access to the WorkTango coach functionality.
   */
  WORKTANGO_COACH_USER = "worktango_coach_user",
}

export type UserCompanyInfo = Required<User>["companyInfo"];

export type KWProfileSettings = Required<UserCompanyInfo>["kwProfileSettings"];
